import { Component, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { AppRoutes } from '@gs-2.0/core';

/**
 * Represents a charity organization with its details
 */
export interface CharityRowData {
  organisationUuid: string;
  creationDate: number;
  rank: number;
  organisationLogoUrl: string | null;
  organizationVideoUrl: string | null;
  organisationName: string;
  executiveSummary: string | null;
  organisationLeader: string | null;
  causeName: string;
  donorNumber: string | null;
  projects: Array<{
    uuid: string;
    name: string;
  }>;
  organisationCountriesNames: string[];
  organisationCategoriesNames: string[];
  languageCode: string;
  badges: Array<{
    uuid: string;
    name: string;
    rank: number;
    image: {
      uuid: string;
      url: string;
      filename: string;
      type: string;
    };
  }>;
}

/**
 * Charity Row component for displaying a single charity in a horizontal layout
 * Figma: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36101
 */
@Component({
  selector: 'app-charity-row',
  standalone: false,
  templateUrl: './charity-row.component.html',
  styleUrls: ['./charity-row.component.scss']
})
export class CharityRowComponent {
  private readonly router = inject(Router);
  /**
   * Charity data to display
   */
  charity = input.required<CharityRowData>();

  /**
   * Whether to show the category badge
   */
  showCategoryBadge = input<boolean>(true);

  /**
   * Whether this is the last row in a list (removes bottom border)
   */
  isLastRow = input<boolean>(false);

  /**
   * Event emitted when the charity row is clicked
   */
  charityClicked = output<CharityRowData>();

  /**
   * Event emitted when the Give button is clicked
   */
  giveClicked = output<CharityRowData>();

  /**
   * Handles the click event on the charity row
   */
  onCharityClick(): void {
    this.charityClicked.emit(this.charity());
  }

  /**
   * Handles the Give button click event
   */
  onGiveClick(): void {
    this.giveClicked.emit(this.charity());
    this.router.navigate([AppRoutes.FUND_GRANT], {
      queryParams: { charityName: this.charity().organisationName },
    });
  }

  /**
   * Wrapper click handler to stop event propagation
   * Prevents Give button click from triggering row click
   */
  onGiveButtonClick(event: Event): void {
    event.stopPropagation();
  }

  /**
   * Gets the first category name to display in the badge
   */
  getCategoryName(): string {
    const charity = this.charity();
    if (charity.organisationCategoriesNames && charity.organisationCategoriesNames.length > 0) {
      return charity.organisationCategoriesNames[0];
    }
    return charity.causeName || '';
  }
}
